import type { AxiosInstance } from 'axios';

import type { TokensService } from './tokens.service';
import type {
  AgentResponse,
  ApplyForAnWhitelistArgs,
  AuthResponse,
  ConfirmEmailArgs,
  CreateSessionArgs,
  DeleteSessionArgs,
  EditSessionTitleArgs,
  GetSessionArgs,
  GoogleAuthRegisterArgs,
  GoogleAuthResponse,
  LoginArgs,
  RefreshTokenArgs,
  RegisterArgs,
  RequestPasswordResetArgs,
  ResetPasswordArgs,
  RunAgentArgs,
  SessionResponse,
  SuccessResponse,
  UpdateUserArgs,
  UpdateUserLanguageArgs,
  UserResponse,
} from './types';

export class Api {
  public constructor(
    public readonly httpClient: AxiosInstance,
    public readonly tokensService: TokensService
  ) {}

  // Auth Methods
  public async refreshToken(args: RefreshTokenArgs): Promise<AuthResponse> {
    const response = await this.httpClient.request({
      method: 'POST',
      url: '/auth/refresh',
      data: args,
    });

    return response.data;
  }

  public async logout(): Promise<SuccessResponse> {
    const response = await this.httpClient.request({
      method: 'POST',
      url: '/auth/logout',
    });

    return response.data;
  }

  public async login(args: LoginArgs): Promise<AuthResponse> {
    const response = await this.httpClient.request({
      method: 'POST',
      url: '/auth/local/login',
      data: args,
    });

    return response.data;
  }

  public async register(args: RegisterArgs): Promise<AuthResponse> {
    const response = await this.httpClient.request({
      method: 'POST',
      url: '/auth/local/register',
      data: args,
    });

    return response.data;
  }

  public async confirmEmail(args: ConfirmEmailArgs): Promise<SuccessResponse> {
    const response = await this.httpClient.request({
      method: 'POST',
      url: '/auth/local/confirm-email',
      data: args,
    });

    return response.data;
  }

  public async resendEmailConfirmation(): Promise<SuccessResponse> {
    const response = await this.httpClient.request({
      method: 'POST',
      url: '/auth/local/resend-email-confirmation',
    });

    return response.data;
  }

  public async requestPasswordReset(
    args: RequestPasswordResetArgs
  ): Promise<SuccessResponse> {
    const response = await this.httpClient.request({
      method: 'POST',
      url: '/auth/local/request-password-reset',
      data: args,
    });

    return response.data;
  }

  public async resetPassword(
    args: ResetPasswordArgs
  ): Promise<SuccessResponse> {
    const response = await this.httpClient.request({
      method: 'POST',
      url: '/auth/local/reset-password',
      data: args,
    });

    return response.data;
  }

  public async googleAuth(
    args: GoogleAuthRegisterArgs
  ): Promise<GoogleAuthResponse> {
    const response = await this.httpClient.request({
      method: 'POST',
      url: '/auth/google',
      data: args,
    });

    return response.data;
  }

  // User Methods
  public async getProfile(): Promise<UserResponse> {
    const response = await this.httpClient.request({
      method: 'GET',
      url: '/users/profile',
    });

    return response.data;
  }

  public async updateProfile(args: UpdateUserArgs): Promise<UserResponse> {
    const response = await this.httpClient.request({
      method: 'PATCH',
      url: '/users/profile',
      data: args,
    });

    return response.data;
  }

  // AI Methods
  public async listAgents(): Promise<string[]> {
    const response = await this.httpClient.request({
      method: 'GET',
      url: '/ai/agents',
    });

    return response.data;
  }

  public async listSessions(): Promise<SessionResponse[]> {
    const response = await this.httpClient.request({
      method: 'GET',
      url: '/ai/sessions',
    });

    return response.data;
  }

  public async createSession(
    args: CreateSessionArgs
  ): Promise<SessionResponse> {
    const response = await this.httpClient.request({
      method: 'POST',
      url: '/ai/sessions',
      data: args,
    });

    return response.data;
  }

  public async getSession(args: GetSessionArgs): Promise<SessionResponse> {
    const response = await this.httpClient.request({
      method: 'POST',
      url: '/ai/sessions/get',
      data: args,
    });

    return response.data;
  }

  public async deleteSession(args: DeleteSessionArgs): Promise<void> {
    await this.httpClient.request({
      method: 'DELETE',
      url: '/ai/sessions',
      data: args,
    });
  }

  public async editSessionTitle(
    args: EditSessionTitleArgs
  ): Promise<SessionResponse> {
    const response = await this.httpClient.request({
      method: 'POST',
      url: '/ai/sessions/edit-title',
      data: args,
    });

    return response.data;
  }

  public async runAgent(args: RunAgentArgs): Promise<AgentResponse> {
    const response = await this.httpClient.request({
      method: 'POST',
      url: '/ai/run',
      data: args,
    });

    return response.data;
  }

  public async runAgentSSE(
    args: RunAgentArgs & {
      streaming: boolean;
    }
  ) {
    const response = await fetch(
      `${this.httpClient.defaults.baseURL}/ai/run-sse`,
      {
        method: 'POST',
        body: JSON.stringify(args),
        headers: {
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.tokensService.getTokens().accessToken}`,
        },
      }
    );

    return response;
  }

  public async applyForAnWhitelist(
    args: ApplyForAnWhitelistArgs
  ): Promise<SuccessResponse> {
    const response = await this.httpClient.request({
      method: 'POST',
      url: '/users/usecase',
      data: args,
    });

    return response.data;
  }

  public async updateUserLanguage(
    args: UpdateUserLanguageArgs
  ): Promise<UserResponse> {
    const response = await this.httpClient.request({
      method: 'PATCH',
      url: '/users/language',
      data: args,
    });

    return response.data;
  }
}
